import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate } from 'react-router-dom';
import useAnalytics from './hooks/useAnalytics';
// Removed useParallax import - no longer needed
import { useAllBlogViewCounts } from './hooks/useBlogAnalytics';
import useScrollPosition from './hooks/useScrollPosition';
import { checkPostHogStatus } from './utils/posthogTest';
import { monitorPostHogEvents, checkPostHogConfiguration } from './utils/posthogDebug';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import ProjectCard from './components/ProjectCard';
import ProjectDetail from './components/ProjectDetail';
import BlogDetail from './components/BlogDetail';
import Shelf from './components/Shelf';
import NotFound from './components/NotFound';
import Footer from './components/Footer';
import { EyeIcon, ReactIcon, HTML5Icon, CSS3Icon, FlutterIcon, FirebaseIcon, MongoDBIcon, GitIcon, FastAPIIcon } from './components/Icons';
import { mockData } from './data/mockData';
import { BlogManager } from './data/blogData';
import './styles/blog.css';

// Blog Detail Route Component
const BlogDetailRoute = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { restoreScrollPosition } = useScrollPosition();
    
    const handleClose = () => {
        navigate('/');
        // Restore scroll position after navigation with a slight delay
        setTimeout(() => {
            restoreScrollPosition('homepage', {
                behavior: 'auto',
                fallbackSelector: '#blog',  // Fallback to blog section if no saved position
                delay: 50  // Additional delay to ensure DOM is ready
            });
        }, 100);
    };
    
    return <BlogDetail slug={slug} onClose={handleClose} />;
};

// Project Detail Route Component
const ProjectDetailRoute = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    // Find project by id
    const project = mockData.projects.find(p => p.id === parseInt(id));
    
    const handleClose = () => {
        navigate('/');
        // Scroll to projects section after navigation
        setTimeout(() => {
            const projectsSection = document.getElementById('work');
            if (projectsSection) {
                projectsSection.scrollIntoView({ behavior: 'smooth' });
            }
        }, 100);
    };
    
    // If project not found, redirect to home
    React.useEffect(() => {
        if (id && !project) {
            navigate('/', { replace: true });
        }
    }, [id, project, navigate]);
    
    return <ProjectDetail project={project} onClose={handleClose} />;
};

// Main Portfolio Component
const Portfolio = () => {
    const [currentPage, setCurrentPage] = useState('intro');
    const navigate = useNavigate();
    const { posthog } = useAnalytics();
    const { saveScrollPosition } = useScrollPosition();

    // Development testing - check PostHog initialization
    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            setTimeout(() => {
                checkPostHogStatus(posthog);
                checkPostHogConfiguration(posthog);
                monitorPostHogEvents(posthog);
                console.log('🧪 Debug utilities available:');
                console.log('- window.posthogDebug.testBlogTracking(window.posthog)');
                console.log('- window.posthogDebug.checkConfig(window.posthog)');
                console.log('- window.testPostHog(window.posthog)');
            }, 1000);
        }
    }, [posthog]);

    const navigateToPage = (pageId) => {
        setCurrentPage(pageId);
    };

    // Scroll-based active section detection
    useEffect(() => {
        const sections = ['intro', 'about', 'work', 'experience', 'stack', 'education', 'blog', 'shelf', 'contact'];
        
        const updateActiveSection = () => {
            const scrollPosition = window.scrollY + 80; // Account for navbar height
            let activeSection = sections[0]; // Default to first section
            
            // Find the section that contains the current scroll position
            for (let i = 0; i < sections.length; i++) {
                const element = document.getElementById(sections[i]);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    const elementTop = rect.top + window.scrollY;
                    
                    // If we're past this section's start, it could be active
                    if (scrollPosition >= elementTop) {
                        activeSection = sections[i];
                    } else {
                        // We've found the first section we haven't reached yet
                        break;
                    }
                }
            }
            
            setCurrentPage(activeSection);
        };

        // Debounced scroll handler to prevent excessive updates
        let timeoutId = null;
        const handleScroll = () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            timeoutId = setTimeout(updateActiveSection, 10);
        };

        // Add scroll listener
        window.addEventListener('scroll', handleScroll, { passive: true });
        
        // Initial check
        updateActiveSection();

        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, []);

    const openBlogDetail = (blogSlug) => {
        // Save current scroll position before navigating
        saveScrollPosition('homepage');
        
        // Navigation only - blog view tracking happens in BlogDetail component
        navigate(`/blogs/${blogSlug}`);
    };

    // Work Section Component
    const WorkSection = React.memo(() => {
        const featuredProjects = mockData.projects.filter(p => p.featured);
        const otherProjects = mockData.projects.filter(p => !p.featured);

        return (
            <section className="section relative overflow-hidden">
                {/* Background blob */}
                <div className="absolute top-40 -right-40 w-[500px] h-[500px] gradient-blob gradient-blob-coral opacity-20" />

                <div className="container relative z-10">
                    <h2 className="section-heading text-charcoal-900 mb-12">
                        Selected <em className="italic-emphasis">work</em>
                    </h2>

                    {/* Featured Projects */}
                    {featuredProjects.length > 0 && (
                        <div className="mb-8">
                            {featuredProjects.map((project) => (
                                <ProjectCard key={project.id} project={project} featured={true} />
                            ))}
                        </div>
                    )}

                    {/* Other Projects Grid */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {otherProjects.map((project) => (
                            <ProjectCard key={project.id} project={project} />
                        ))}
                    </div>
                </div>
            </section>
        );
    });

    // Experience Section Component
    const ExperienceSection = React.memo(() => {
        return (
            <section className="section bg-white">
                <div className="container">
                    <h2 className="section-heading text-charcoal-900 mb-12">
                        Work <em className="italic-emphasis">experience</em>
                    </h2>

                    <div className="relative">
                        {/* Timeline line */}
                        <div className="absolute left-3 top-0 bottom-0 w-px bg-border-light hidden md:block" />

                        <div className="space-y-8">
                            {mockData.workExperience.map((job, index) => (
                                <div key={job.id} className="relative flex gap-8">
                                    {/* Timeline dot */}
                                    <div className="hidden md:flex flex-col items-center">
                                        <div className="w-6 h-6 bg-coral-500 rounded-full flex items-center justify-center z-10">
                                            <div className="w-2 h-2 bg-white rounded-full" />
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 bg-cream-50 rounded-2xl p-6 md:p-8 border border-border-light hover:shadow-card transition-all duration-300">
                                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                                            <div>
                                                <h3 className="font-serif text-xl text-charcoal-900">{job.position}</h3>
                                                <p className="text-coral-600 font-medium">{job.company}</p>
                                            </div>
                                            <div className="text-sm text-charcoal-500">
                                                <div className="font-medium">{job.duration}</div>
                                                <div>{job.location}</div>
                                            </div>
                                        </div>

                                        <p className="text-charcoal-600 mb-4">{job.description}</p>

                                        <div className="mb-4">
                                            <h4 className="text-sm font-medium text-charcoal-900 mb-2">Key Achievements</h4>
                                            <ul className="space-y-1">
                                                {job.achievements.slice(0, 5).map((achievement, i) => (
                                                    <li key={i} className="flex items-start gap-2 text-sm text-charcoal-600">
                                                        <span className="w-1 h-1 bg-coral-400 rounded-full mt-2 flex-shrink-0" />
                                                        {achievement}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div className="flex flex-wrap gap-2">
                                            {job.technologies.map((tech, i) => (
                                                <span key={i} className="px-2.5 py-1 bg-white text-xs text-charcoal-600 rounded-full border border-border-light">
                                                    {tech}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        );
    });

    // Tech Stack Section Component
    const TechStackSection = React.memo(() => {
        const getTechConfig = (skill) => {
            const techMap = {
                'Flutter': { icon: FlutterIcon, hasOwnColors: true },
                'ReactJS': { icon: ReactIcon, hasOwnColors: false },
                'HTML': { icon: HTML5Icon, hasOwnColors: false },
                'CSS': { icon: CSS3Icon, hasOwnColors: false },
                'Git': { icon: GitIcon, hasOwnColors: false },
                'FastAPI': { icon: FastAPIIcon, hasOwnColors: false },
                'Firebase': { icon: FirebaseIcon, hasOwnColors: true },
                'MongoDB': { icon: MongoDBIcon, hasOwnColors: false }
            };
            return techMap[skill] || { icon: ReactIcon, hasOwnColors: false };
        };

        // Group skills by category
        const groupedSkills = mockData.stack.reduce((acc, tech) => {
            const category = tech.category || 'Other';
            if (!acc[category]) acc[category] = [];
            acc[category].push(tech);
            return acc;
        }, {});

        return (
            <section className="section relative overflow-hidden">
                <div className="absolute -bottom-20 -left-40 w-[400px] h-[400px] gradient-blob gradient-blob-peach opacity-30" />

                <div className="container relative z-10">
                    <h2 className="section-heading text-charcoal-900 mb-12">
                        Tech <em className="italic-emphasis">stack</em>
                    </h2>

                    {/* Mobile: compact card grid */}
                    <div className="grid grid-cols-2 gap-3 md:hidden">
                        {mockData.stack.map((tech, index) => {
                            const techConfig = getTechConfig(tech.name);
                            const IconComponent = techConfig.icon;

                            return (
                                <div
                                    key={index}
                                    className="bg-white border border-border-light rounded-xl p-4 flex items-center gap-3"
                                >
                                    <div className="w-10 h-10 bg-cream-100 rounded-lg flex items-center justify-center shrink-0">
                                        <IconComponent className="w-6 h-6" />
                                    </div>
                                    <span className="text-sm font-medium text-charcoal-900">{tech.name}</span>
                                </div>
                            );
                        })}
                    </div>

                    {/* Desktop: card grid */}
                    <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {mockData.stack.map((tech, index) => {
                            const techConfig = getTechConfig(tech.name);
                            const IconComponent = techConfig.icon;

                            return (
                                <div
                                    key={index}
                                    className="group bg-white border border-border-light rounded-xl p-5 hover:shadow-card hover:-translate-y-1 transition-all duration-300"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-cream-100 rounded-xl flex items-center justify-center group-hover:bg-coral-50 transition-all duration-300">
                                            <IconComponent className="w-7 h-7 transition-transform duration-300 group-hover:rotate-12" />
                                        </div>
                                        <div>
                                            <h3 className="font-medium text-charcoal-900">{tech.name}</h3>
                                            <p className="text-xs text-charcoal-500">{tech.category}</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        );
    });

    // Education Section Component
    const EducationSection = React.memo(() => {
        return (
            <section className="section bg-white">
                <div className="container">
                    <h2 className="section-heading text-charcoal-900 mb-12">
                        <em className="italic-emphasis">Education</em>
                    </h2>

                    <div className="space-y-8">
                        {mockData.education.map(edu => (
                            <div key={edu.id} className="bg-cream-50 rounded-2xl p-6 md:p-8 border border-border-light hover:shadow-card transition-all duration-300">
                                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                                    <div>
                                        <h3 className="font-serif text-xl text-charcoal-900">{edu.degree}</h3>
                                        <p className="text-coral-600 font-medium">{edu.institution}</p>
                                    </div>
                                    <div className="text-sm text-charcoal-500 md:text-right">
                                        <div className="font-medium">{edu.duration}</div>
                                        <div>{edu.location} &bull; {edu.grade}</div>
                                    </div>
                                </div>

                                <div className="w-full h-px bg-gradient-to-r from-transparent via-border-light to-transparent mb-4" />

                                <p className="text-charcoal-600 mb-4">{edu.description}</p>

                                <div>
                                    <h4 className="text-sm font-medium text-charcoal-900 mb-2">Achievements</h4>
                                    <ul className="space-y-1">
                                        {edu.achievements.map((achievement, index) => (
                                            <li key={index} className="flex items-start gap-2 text-sm text-charcoal-600">
                                                <span className="w-1 h-1 bg-coral-400 rounded-full mt-2 flex-shrink-0" />
                                                {achievement}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    });

    // Blog Section Component
    const BlogSection = React.memo(() => {
        const blogPosts = BlogManager.getRecentPosts(4);
        const { viewCounts, loading: viewCountsLoading } = useAllBlogViewCounts();

        return (
            <section className="section bg-white">
                <div className="container">
                    <div className="flex items-center justify-between">
                        <h2 className="section-heading text-charcoal-900">
                            Latest <em className="italic-emphasis">articles</em>
                        </h2>
                        <span className="text-sm text-charcoal-500">{BlogManager.getAllPosts().length} articles</span>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {blogPosts.map(post => {

                            const category = BlogManager.getCategoryById(post.category);
                            return (
                                <article
                                    key={post.id}
                                    className="group cursor-pointer bg-cream-50 border border-border-light rounded-2xl overflow-hidden hover:shadow-card hover:-translate-y-1 transition-all duration-300"
                                    onClick={() => openBlogDetail(post.slug)}
                                >
                                    {/* Blog Image */}
                                    <div className="aspect-[16/9] overflow-hidden bg-cream-100">
                                        {post.image ? (
                                            <img
                                                src={post.image}
                                                alt={post.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-coral-100 to-cream-200">
                                                <span className="font-serif text-3xl text-coral-400">
                                                    {post.title.split(' ').map(word => word[0]).join('').slice(0, 2)}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Blog Content */}
                                    <div className="p-6">
                                        <div className="flex items-center gap-3 mb-3">
                                            {category && (
                                                <span className="px-2.5 py-1 bg-coral-50 text-coral-600 text-xs font-medium rounded-full">
                                                    {category.name}
                                                </span>
                                            )}
                                            <span className="text-charcoal-400 text-xs">{post.readTime}</span>
                                        </div>

                                        <h3 className="font-serif text-xl text-charcoal-900 mb-2 group-hover:text-coral-600 transition-colors line-clamp-2">
                                            {post.title}
                                        </h3>

                                        <p className="text-charcoal-600 text-sm mb-4 line-clamp-2">{post.excerpt}</p>

                                        <div className="flex items-center justify-between text-xs text-charcoal-500">
                                            <span>{new Date(post.publishedAt).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric'
                                            })}</span>
                                            <span className="flex items-center gap-1">
                                                {viewCountsLoading ? (
                                                    <span className="animate-pulse">...</span>
                                                ) : (
                                                    <>
                                                        <EyeIcon className="w-3 h-3" />
                                                        {(viewCounts[post.slug] !== undefined ? viewCounts[post.slug] : post.views)?.toLocaleString()}
                                                    </>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>

                </div>
            </section>
        );
    });

    // Shelf Section Component
    const ShelfSection = React.memo(() => {
        const previewBooks = mockData.shelf.books.slice(0, 3);
        const total = mockData.shelf.books.length + mockData.shelf.papers.length;

        return (
            <section className="section bg-white">
                <div className="container">
                    <h2 className="section-heading text-charcoal-900 mb-6">
                        My <em className="italic-emphasis">shelf</em>
                    </h2>

                    <ul className="space-y-0 mb-8">
                        {previewBooks.map((book, i) => (
                            <li
                                key={book.id}
                                className={`py-5 ${i < previewBooks.length - 1 ? 'border-b border-border-light' : ''}`}
                            >
                                <p className="font-serif text-lg text-charcoal-900">{book.title}</p>
                                <p className="text-sm text-charcoal-500 mt-0.5">{book.author}</p>
                            </li>
                        ))}
                    </ul>

                    <button
                        onClick={() => {
                            saveScrollPosition('homepage');
                            navigate('/shelf');
                        }}
                        className="flex items-center gap-2 text-sm font-medium text-charcoal-600 hover:text-coral-600 transition-colors group"
                    >
                        Browse the full shelf
                        <svg className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                    </button>
                </div>
            </section>
        );
    });

    // Contact Section Component
    const ContactSection = React.memo(() => {
        const { trackContactInteraction, trackSocialClick } = useAnalytics();

        const handleEmailClick = () => {
            trackContactInteraction('email_contact', mockData.personalInfo.email);
        };

        const handleLinkedInClick = () => {
            trackSocialClick('linkedin_contact', mockData.personalInfo.socialLinks.linkedin);
        };

        return (
            <section className="section relative overflow-hidden">
                {/* Background blobs */}
                <div className="absolute top-0 left-1/4 w-[400px] h-[400px] gradient-blob gradient-blob-coral opacity-20" />
                <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] gradient-blob gradient-blob-peach opacity-30" />

                <div className="container relative z-10">
                    <div className="max-w-2xl mx-auto text-center">
                        <h2 className="text-display text-charcoal-900 mb-6">
                            Let's <em className="italic-emphasis">connect</em>
                        </h2>
                        <p className="text-lg text-charcoal-600 mb-10">
                            I'm always open to interesting opportunities and projects. 
                            If you think we'd work well together, I'd love to hear from you.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <a
                                href={`mailto:${mockData.personalInfo.email}`}
                                onClick={handleEmailClick}
                                className="btn-primary"
                            >
                                Get In Touch
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                </svg>
                            </a>
                            <a
                                href={mockData.personalInfo.socialLinks.linkedin}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={handleLinkedInClick}
                                className="btn-secondary"
                            >
                                Connect on LinkedIn
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        );
    });

    // Define sections for sticky navigation
    const sections = ['intro', 'about', 'work', 'experience', 'stack', 'education', 'blog', 'shelf', 'contact'];

    // Removed hero parallax for clean scrolling experience

    return (
        <div className="min-h-screen bg-cream-100 text-charcoal-900 relative">
            <Navbar currentPage={currentPage} navigateToPage={navigateToPage} personalInfo={mockData.personalInfo} />
            <main className="relative z-10">
                <div id="intro">
                    <HeroSection />
                </div>
                <div id="about">
                    <AboutSection />
                </div>
                <div id="work"><WorkSection /></div>
                <div id="experience">
                    <ExperienceSection />
                </div>
                <div id="education">
                    <EducationSection />
                </div>
                <div id="stack">
                    <TechStackSection />
                </div>
                
                <div id="blog">
                    <BlogSection />
                </div>
                <div id="shelf">
                    <ShelfSection />
                </div>
                <div id="contact"><ContactSection /></div>
            </main>
            <Footer />
        </div>
    );
};

// Main App Component with Routing
function App() {
    return (
        <Router>
            <Routes>
                <Route path="/" element={<Portfolio />} />
                <Route path="/projects/:id" element={<ProjectDetailRoute />} />
                <Route path="/blogs/:slug" element={<BlogDetailRoute />} />
                <Route path="/shelf" element={<Shelf />} />
                <Route path="*" element={<NotFound />} />
            </Routes>
        </Router>
    );
}

export default App;